import { getYear } from 'date-fns';

import uniq from 'lodash/uniq';

export enum Channel {
  eCommerce = 'eCommerce',
  retail = 'retail',
  b2b = 'b2b',
}

export interface PaymentSignature {
  key: string;
  signature: string;
  tenantId: string;
  token: string;
}

export enum PaymentMethodType {
  CreditCard = 'CreditCard',
  HSA = 'HSA',
  PayPal = 'PayPal',
}

export interface PaymentGateways {
  hostedPageId: string;
  creditCardGatewayName: string;
  hsaGatewayName: string;
  paypalGatewayName?: string;
}
// TODO: cannot use this, since capitalization leaves PayPal to payPalGateway, while we want paypalGateway
// & Record<Uncapitalize<`${PaymentMethodType}GatewayName`>, string>;

export enum CardType {
  Visa = 'Visa',
  MasterCard = 'MasterCard',
  AmericanExpress = 'AmericanExpress',
  Discover = 'Discover',
  Diners = 'Diners',
  JCB = 'JCB',
}

export enum FlowType {
  Signup = 'Signup',
  Renew = 'Renew',
  AddOrUpdatePaymentMethod = 'AddOrUpdatePaymentMethod',
}

export type PrepopulateFields = Partial<{
  creditCardCountry: string;
  creditCardState: string;
  creditCardAddress1: string;
  creditCardAddress2: string;
  creditCardCity: string;
  creditCardPostalCode: string;
}>;

export type HostedPageParams = PaymentSignature & {
  id: string;
  style: 'inline';
  submitEnabled: false;
  locale: string;
  url: string;
  paymentGateway: string;
  field_currency: string;
  field_passthrough1: string; // Gateway name
  field_passthrough2: FlowType;
};

export enum EntitlementType {
  LIFETIME = 'LIFETIME',
  FIXED_12_MONTH = 'FIXED_12_MONTH',
  FIXED_24_MONTH = 'FIXED_24_MONTH',
  FIXED_36_MONTH = 'FIXED_36_MONTH',
}

export interface Entitlement {
  type: EntitlementType;
}

export enum MembershipType {
  B2B = 'B2B',
  B2C = 'B2C',
  LEGACY_HW = 'LEGACY_HW',
  LIFETIME = 'LIFETIME',
  UNFINISHED = 'UNFINISHED', // not completed signup flow
  UNVERIFIED = 'UNVERIFIED', // not email_verified
}

export enum SignupMembershipType {
  ENTITLEMENT = 'ENTITLEMENT',
  LEGACY = 'LEGACY',
  PAYING = 'PAYING',
}

type PaymentMethodStatus = 'Active' | 'Closed' | 'Scrubbed';

export interface CreditCardPaymentMethod {
  type: PaymentMethodType.CreditCard;
  status: PaymentMethodStatus;
  cardNumber: string;
  isDefault: boolean;
  isExpired: boolean;
  isReusableInRenew: boolean;
  cardType: CardType;
}

interface PayPalPaymentMethod {
  type: PaymentMethodType.PayPal;
  id: string;
  status: PaymentMethodStatus;
  isDefault: boolean;
  isReusableInRenew: boolean;
}

export type PaymentMethod = PayPalPaymentMethod | CreditCardPaymentMethod;

export type SubscriptionType = 'CHARGEABLE' | 'LIFETIME_FREE';
export type RenewalState = 'PAYMENT_METHOD_REQUIRED' | 'NO_PAYMENT_METHOD_REQUIRED' | 'RENEWAL_FORBIDDEN';

export interface RecurringFee {
  amount: number;
  currency: Currency;
  billingPeriod: BillingPeriod;
  trialLength?: number;
}

export enum SubscriptionState {
  TRIAL = 'TRIAL',
  MEMBER = 'MEMBER',
  LIFETIME = 'LIFETIME',
  PENDING = 'PENDING',
  EXPIRED = 'EXPIRED',
}

export interface PrepaidPeriod {
  start: string;
  end: string;
  lengthInMonths: number;
}
interface PrepaidPeriods {
  current: PrepaidPeriod | null;
  future: PrepaidPeriod | null;
}

export enum PayerType {
  D2C = 'D2C',
  B2B = 'B2B',
  NOT_APPLICABLE = 'NOT_APPLICABLE',
}

export enum SubscriptionModel {
  RECURRING = 'RECURRING',
  FIXED_TERM = 'FIXED_TERM',
  NOT_APPLICABLE = 'NOT_APPLICABLE',
}

export enum PlanType {
  TRIAL = 'TRIAL',
  COMPENSATION = 'COMPENSATION',
  PREPAID = 'PREPAID',
  ANNUAL = 'ANNUAL',
  MONTHLY = 'MONTHLY',
  LIFETIME = 'LIFETIME',
  NOT_APPLICABLE = 'NOT_APPLICABLE',
}

export enum PlanStatus {
  PAST = 'PAST',
  PRESENT = 'PRESENT',
  FUTURE = 'FUTURE',
}

export interface Plan {
  currency: Currency | null;
  endDate: string | null;
  payerType: PayerType;
  periodLength: number;
  periodType: BillingPeriod;
  price: number | null;
  startDate: string | null;
  status: PlanStatus;
  subscriptionId: string;
  subscriptionModel: SubscriptionModel;
  type: PlanType;
}

export interface Membership {
  product: string;
  name: string;
  nextPaymentDate: string | null;
  pendingCancellation: boolean;
  inDunning: boolean;
  active: boolean;
  plans: Plan[];
}

export enum UserState {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  LEGACY = 'LEGACY',
  PROSPECT = 'PROSPECT',
  PENDING = 'PENDING',
}

export interface UserContext {
  state: UserState;
  payerType: PayerType;
  subscriptionModel: SubscriptionModel;
  planType: PlanType;
}

export enum UserPermission {
  canAccessHub = 'canAccessHub',
  canAccessSignUp = 'canAccessSignUp',
  canChangePlan = 'canChangePlan',
  canCancel = 'canCancel',
  canUpdatePaymentMethod = 'canUpdatePaymentMethod',
  canViewBilling = 'canViewBilling',
  canRenew = 'canRenew',
  canQuickRenew = 'canQuickRenew',
  canConvertToD2C = 'canConvertToD2C',
  canChangeCurrency = 'canChangeCurrency',
}

export interface AccessProfile {
  userContext: UserContext;
  permissions: UserPermission[];
}

export interface Subscription {
  id: string;
  nextBillingDate?: string; // TODO correct the typing here to null (MMBER-5645)
  nextPaymentDate?: string; // null in expired or signup user
  endDate?: string;
  startDate?: string;
  pendingCancellation: boolean;
  recurringFee: RecurringFee | null;
  alternativeBillingPeriods: RecurringFee[] | null;
  channel: Channel;
  subscriptionType: SubscriptionType;
  renewalState: RenewalState;
  subscriptionState: SubscriptionState;
  trialExtensionLengthInMonths: number;
  prepaidPeriods?: PrepaidPeriods | null; // shown only for currentSubscription
  pendingCompMonths?: number | null; // shown only for currentSubscription
}

export interface MembershipStatus {
  type: SignupMembershipType;
  subscriptionFound: boolean;
  signupComplete: boolean;
}

export interface ApiProduct {
  trial: Period | null;
  membership: Period;
  pricing: Pricing;
}

export type RatePlan = RecurringFee;

export interface Country {
  code: string;
  pricing: Pricing;
}

export interface Pricing {
  currency: Currency;
  amount: number;
}

export interface Period {
  length: number;
  periodType: BillingPeriod;
}

export enum BillingPeriod {
  Days = 'Days',
  Weeks = 'Weeks',
  Months = 'Months',
  Years = 'Years',
}

export interface Contact {
  firstName: string;
  lastName: string;
  address1: string;
  address2?: string;
  city: string;
  state?: string;
  postalCode: string;
  country: string;
  telephone?: string;
  personalEmail: string;
  addressValidated: boolean;
}

export type AddressValidationContact = Omit<Contact, 'addressValidated'>;

export interface BillingAddress {
  address1: string;
  city: string;
  country: string;
  postalCode: string;
  state?: string;
}

export interface Address {
  firstName: string;
  lastName: string;
  address1: string;
  address2?: string;
  city: string;
  state?: string;
  country: string;
  postalCode: string;
  isoAlpha3Code?: string;
}

// country list for address page
export interface Region {
  name: string;
  code: string;
}

export type Currency =
  | 'AUD'
  | 'CAD'
  | 'EUR'
  | 'GBP'
  | 'JPY'
  | 'USD'
  | 'CHF'
  | 'HKD'
  | 'MXN'
  | 'NZD'
  | 'SGD'
  | 'KRW'
  | 'AED'
  | 'SAR'
  | 'MYR';

export interface CountryWeSellTo {
  countryCode: string;
  name: string;
  regions: Region[];
  isoAlpha3Code: string;
}

export type AppType = 'signup' | 'hub';

export interface MembershipOfferings {
  channel: Channel;
  entitlement: Entitlement;
  prepaidMonths: number;
  customerId: string;
  customerName: string;
  membershipStatus: MembershipStatus;
  eligibleForPartnership: boolean;
}

export enum AccessUserTypes {
  // b2c types
  ACTIVE_B2C_ALL = 'ACTIVE_B2C_ALL', // selects all active b2c types
  ACTIVE_B2C_ADDRESS_PAYMENT_METHOD = 'ACTIVE_B2C_ADDRESS_PAYMENT_METHOD',
  ACTIVE_B2C_NO_ADDRESS_PAYMENT_METHOD = 'ACTIVE_B2C_NO_ADDRESS_PAYMENT_METHOD',
  ACTIVE_B2C_ADDRESS_NO_PAYMENT_METHOD = 'ACTIVE_B2C_ADDRESS_NO_PAYMENT_METHOD',
  ACTIVE_B2C_NO_ADDRESS_NO_PAYMENT_METHOD = 'ACTIVE_B2C_NO_ADDRESS_NO_PAYMENT_METHOD',

  INACTIVE_B2C_ALL = 'INACTIVE_B2C_ALL', // selects all inactive b2c types
  INACTIVE_B2C_ADDRESS_PAYMENT_METHOD = 'INACTIVE_B2C_ADDRESS_PAYMENT_METHOD',
  INACTIVE_B2C_NO_ADDRESS = 'INACTIVE_B2C_NO_ADDRESS',
  INACTIVE_B2C_ADDRESS_NO_PAYMENT_METHOD = 'INACTIVE_B2C_ADDRESS_NO_PAYMENT_METHOD',
  INACTIVE_B2C_NO_ADDRESS_NO_PAYMENT_METHOD = 'INACTIVE_B2C_NO_ADDRESS_NO_PAYMENT_METHOD',

  PENDING_B2C = 'PENDING_B2C',
  INVALID_CURRENCY_ACTIVE_B2C = 'INVALID_CURRENCY_ACTIVE_B2C',

  // entitlement (b2b/lifetime) types
  ACTIVE_B2B = 'ACTIVE_B2B',
  INACTIVE_B2B = 'INACTIVE_B2B',
  LIFETIME = 'LIFETIME',

  // gen 2 types
  LEGACY = 'LEGACY',

  // no access to hub except pw reset page
  UNFINISHED = 'UNFINISHED', // not completed signup flow
  UNVERIFIED = 'UNVERIFIED', // not email_verified
}

export enum PaymentMethodState {
  ACTIVE = 'Active',
  NO_PAYMENT = 'No Payment Method',
  EXPIRED = 'Expired',
}

enum CompensationType {
  COMPENSATION_MONTH = 'COMPENSATION_MONTH',
  DUNNING_CANCELLATION = 'DUNNING_CANCELLATION',
  OTHER = 'OTHER',
  REFUND = 'REFUND',
}

export interface Invoice {
  id: string;
  amount: number;
  balance: number;
  date: string; // ISO date string
  compensationType: CompensationType | null;
  compensationAmount: number;
}

export interface AnnualReport {
  id: string;
  year: number;
}

export const invoicesToAnnualReport = (invoices: Invoice[]): AnnualReport[] => {
  const years = uniq(invoices.map((inv) => getYear(inv.date)));
  return years.map((year) => {
    const annualReport: AnnualReport = {
      id: year.toString(),
      year,
    };
    return annualReport;
  });
};

export type Field = 'address1' | 'address2' | 'postalCode' | 'city' | 'state' | 'country';

export interface Problem {
  field: Field | null;
  message: string;
  correctedValue: string | null;
}

export enum MemberDataExportStatus {
  scheduled = 'scheduled',
  processing = 'processing',
  failed = 'failed',
  cancelled = 'cancelled',
  completed = 'completed',
}

interface BaseMemberDataExport {
  id: string;
  status: MemberDataExportStatus;
  createdAt: string;
}

export interface ScheduledMemberDataExport extends BaseMemberDataExport {
  status: MemberDataExportStatus.scheduled;
  scheduledAt: string;
}

export interface ProcessingMemberDataExport extends BaseMemberDataExport {
  status: MemberDataExportStatus.processing;
}

export interface FailedMemberDataExport extends BaseMemberDataExport {
  status: MemberDataExportStatus.failed;
}

export interface CancelledMemberDataExport extends BaseMemberDataExport {
  status: MemberDataExportStatus.cancelled;
}

export interface CompletedMemberDataExport extends BaseMemberDataExport {
  status: MemberDataExportStatus.completed;
  completedAt: string;
  downloadUrlExpiresAt: string;
}

export type MemberDataExport =
  | ScheduledMemberDataExport
  | ProcessingMemberDataExport
  | FailedMemberDataExport
  | CancelledMemberDataExport
  | CompletedMemberDataExport;

export type InitiateMemberDataExportOutput = 'ALREADY_IN_PROGRESS' | 'REQUEST_CREATED' | 'REQUEST_FAILED';

export interface UserAddressForm {
  billingAddress: Address;
  shippingAddress: Address;
  isUnifiedAddress: boolean;
}
